/**
 * 「忙完回家」的查询与命令（见 ./comeHome.service）。读口从哪来照 ./usePetFriends：外面用 ComeHomeServiceProvider 换一个（测试用）；
 * 没有就按数据模式选，live 用全站唯一的 HTTP 客户端，演示用报「还没开放」的那一套。
 * 能力表 pets.plan_requests：available 才给按钮和确认条；disabled 时只藏这两样，当前安排和撤回照 GET current 显示（F0 CR-1 修订）。
 */
import { createContext, useContext, useRef, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CapabilityStatus } from "@/shared/contracts";
import { apiClient } from "@/shared/api/client";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { demoComeHomeService, liveComeHomeService, type ComeHomeAsk, type ComeHomeService } from "./comeHome.service";

const ServiceContext = createContext<ComeHomeService | null>(null);

export function ComeHomeServiceProvider({ service, children }: { service: ComeHomeService; children: ReactNode }) {
  return <ServiceContext.Provider value={service}>{children}</ServiceContext.Provider>;
}

let live: ComeHomeService | null = null;
let demo: ComeHomeService | null = null;

export function useComeHomeService(): ComeHomeService {
  const injected = useContext(ServiceContext);
  if (injected) return injected;
  if (env.dataMode === "fixture") return (demo ??= demoComeHomeService());
  return (live ??= liveComeHomeService(apiClient));
}

export const comeHomeKey = (userId: string | null, petId: string | null) => ["come-home", userId ?? "-", petId ?? "-"] as const;

/**
 * 能力表里 pets.plan_requests 此刻的状态；演示模式、还没读到、读失败、没启用都是 null（当作不给按钮）。
 * platform 服务只在真去读的时候才取：通讯器页的用例按页面实际用到的服务给替身，渲染时就取会让整页挂掉。
 */
export function usePlanRequestsCapability(enabled = true): CapabilityStatus | null {
  const services = useServices();
  const meta = useQuery({ queryKey: queryKeys.meta, queryFn: () => services.platform.meta(), staleTime: 60_000, enabled: enabled && env.dataMode === "live" });
  return meta.data?.capabilities.find((capability) => capability.key === "pets.plan_requests")?.status ?? null;
}

export function useComeHomeCurrent(userId: string | null, petId: string | null, enabled: boolean) {
  const service = useComeHomeService();
  return useQuery({
    queryKey: comeHomeKey(userId, petId),
    queryFn: ({ signal }) => service.current(petId!, signal),
    enabled: enabled && Boolean(petId),
    refetchInterval: 30_000,
    retry: false,
  });
}

/** 同一次点击重试时用同一个幂等键；成功或改了要求以后换新的。 */
export function useComeHomeCommands(userId: string | null, petId: string | null) {
  const service = useComeHomeService();
  const queryClient = useQueryClient();
  const askKey = useRef(newIdempotencyKey("come-home"));
  const cancelKey = useRef(newIdempotencyKey("come-home-cancel"));
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: comeHomeKey(userId, petId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.home });
  };
  const ask = useMutation({
    mutationFn: (body: ComeHomeAsk) => service.ask(petId!, body, askKey.current),
    onSuccess: () => {
      askKey.current = newIdempotencyKey("come-home");
      refresh();
    },
  });
  const cancel = useMutation({
    mutationFn: ({ requestId, version }: { requestId: string; version: number }) => service.cancel(petId!, requestId, version, cancelKey.current),
    onSuccess: () => {
      cancelKey.current = newIdempotencyKey("come-home-cancel");
      refresh();
    },
    onError: refresh,
  });
  return { ask, cancel };
}
